import { Plus, Trash2 } from "lucide-react";
import { ImageUpload } from "@/components/DesignPanel";
import { assetUrl } from "@/lib/api";

const PRESET_PX = { sm: 24, md: 36, lg: 48 };

const LogoSize = ({ logo, preset, onChange, i }) => (
  <div className="sm:col-span-3 flex flex-wrap items-center gap-4">
    <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Height</span>
    <input type="range" min={16} max={120} data-testid={`sponsor-height-${i}`} value={logo.height || PRESET_PX[preset] || 36}
      onChange={(e) => onChange({ height: parseInt(e.target.value, 10) })} className="flex-1 min-w-[140px] accent-indigo-500" />
    <span data-testid={`sponsor-height-value-${i}`} className="text-xs text-slate-400 w-24">{logo.height ? `${logo.height}px` : "Default size"}</span>
    {logo.height > 0 && <button type="button" data-testid={`sponsor-height-reset-${i}`} onClick={() => onChange({ height: 0 })} className="text-xs text-indigo-300 hover:underline">Reset</button>}
    {logo.path && (
      <div className="w-full h-[130px] rounded-md bg-white/95 grid place-items-center overflow-hidden" data-testid={`sponsor-height-preview-${i}`}>
        <img src={assetUrl(logo.path)} alt="" style={{ height: `${logo.height || PRESET_PX[preset] || 36}px` }} className="w-auto max-w-[240px] object-contain" />
      </div>
    )}
  </div>
);

const inputCls = "w-full h-11 px-4 rounded-md bg-[#0B0F17] border border-[#242C3D] focus:border-indigo-500 focus:outline-none text-sm transition-colors";
const SIZES = [["sm", "Small"], ["md", "Medium"], ["lg", "Large"]];

export const SponsorsEditor = ({ value: sp, onChange }) => {
  const set = (p) => onChange({ ...sp, ...p });
  const setLogo = (i, p) => set({ logos: sp.logos.map((l, j) => (j === i ? { ...l, ...p } : l)) });
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-end">
        <div>
          <label className="block font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">Label (optional)</label>
          <input data-testid="sponsors-label-input" className={inputCls} value={sp.label} maxLength={60} placeholder="Sponsored by" onChange={(e) => set({ label: e.target.value })} />
        </div>
        <div className="flex gap-1 p-1 rounded-lg bg-[#0B0F17] border border-[#242C3D]">
          {SIZES.map(([v, t]) => (
            <button key={v} type="button" data-testid={`sponsors-size-${v}`} onClick={() => set({ size: v })}
              className={`h-9 px-3 rounded-md text-sm transition-colors ${sp.size === v ? "bg-indigo-500 text-white" : "text-slate-400 hover:text-white"}`}>{t}</button>
          ))}
        </div>
      </div>
      {sp.logos.map((l, i) => (
        <div key={i} data-testid={`sponsor-row-${i}`} className="grid sm:grid-cols-[160px_1fr_auto] gap-4 items-center rounded-md border border-[#242C3D] p-3">
          <ImageUpload value={l.path} onChange={(path) => setLogo(i, { path })} testid={`sponsor-logo-upload-${i}`} emptyLabel="Upload logo" />
          <input data-testid={`sponsor-url-input-${i}`} className={inputCls} value={l.url} placeholder="Click-through link (optional) https://…" onChange={(e) => setLogo(i, { url: e.target.value })} />
          <button type="button" data-testid={`sponsor-remove-${i}`} onClick={() => set({ logos: sp.logos.filter((_, j) => j !== i) })}
            className="w-10 h-10 rounded-md grid place-items-center text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors" aria-label="Remove sponsor"><Trash2 className="w-4 h-4" /></button>
          <LogoSize logo={l} preset={sp.size} i={i} onChange={(p) => setLogo(i, p)} />
        </div>
      ))}
      {sp.logos.length < 3 && (
        <button type="button" data-testid="sponsor-add-button" onClick={() => set({ logos: [...sp.logos, { path: "", url: "", height: 0 }] })}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-dashed border-[#242C3D] hover:border-indigo-500 text-sm text-slate-300 transition-colors">
          <Plus className="w-4 h-4" /> Add sponsor logo
        </button>
      )}
    </div>
  );
};
